import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
interface ExitProps {
  onOwnerContactOpen: () => void;
}
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.6,
    ease: 'easeOut'
  }
};
export function Exit({
  onOwnerContactOpen
}: ExitProps) {
  return <main className="bg-white min-h-screen" data-id="element-326">
      {/* SECTION 1: HERO */}
      <section className="relative min-h-screen flex flex-col items-center justify-center px-6 py-20 bg-white text-center" data-id="element-327">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="max-w-5xl mx-auto flex flex-col items-center" data-id="element-328">
          {/* Logo */}
          <img src="https://cdn.magicpatterns.com/uploads/vdgv36ytuJeDtCVoSnd1Nw/DF_logo_.png" alt="Deal Flow" className="h-16 md:h-20 mb-8" style={{
          filter: 'invert(1) hue-rotate(180deg)'
        }} data-id="element-329" />

          {/* Subheadline */}
          <p className="text-deal-green text-base md:text-lg lg:text-xl italic mb-4 md:mb-6 font-bold" data-id="element-330">
            If you're doing above $5M in annual revenue...
          </p>

          {/* Headline */}
          <h1 className="text-3xl md:text-6xl lg:text-7xl font-serif font-bold italic text-gray-900 leading-[1.1] mb-4 md:mb-8 tracking-tight" data-id="element-331">
            Get A Guaranteed & Qualified Cash Buyer in 90 Days
          </h1>

          {/* Body */}
          <p className="text-gray-500 text-sm md:text-lg lg:text-xl italic max-w-3xl leading-relaxed mb-6 md:mb-12" data-id="element-332">
            We'll{' '}
            <span className="font-bold text-gray-700" data-id="element-333">value your business</span>
            , guarantee a{' '}
            <span className="underline text-gray-700" data-id="element-334">sales price</span>, and
            connect you to a cash buyer from our{' '}
            <span className="font-bold text-gray-700" data-id="element-335">
              6,000+ family offices, PE funds, and strategic buyers
            </span>{' '}
            - privately, on your terms, with{' '}
            <span className="underline text-gray-700" data-id="element-336">no listings</span> or{' '}
            <span className="underline text-gray-700" data-id="element-337">broker fees</span>.
          </p>

          {/* Video */}
          <div className="w-full max-w-5xl mb-8 md:mb-12" style={{
          position: 'relative',
          paddingTop: '56.25%'
        }} data-id="element-338">
            <iframe src="https://iframe.mediadelivery.net/embed/460652/621cb731-c72c-4da2-b46a-5e6190f395c7?autoplay=true&loop=false&muted=false&preload=true&responsive=true" loading="lazy" style={{
            border: 0,
            position: 'absolute',
            top: 0,
            height: '100%',
            width: '100%'
          }} allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;" allowFullScreen className="rounded-xl" data-id="element-339" />
          </div>

          {/* CTA Button */}
          <button onClick={onOwnerContactOpen} className="bg-deal-green text-white font-bold text-base px-10 py-5 rounded-lg hover:bg-deal-green-dark transition-colors duration-300 flex items-center gap-2" data-id="element-340">
            Book Your Confidential Consultation
            <ArrowRightIcon className="w-5 h-5" data-id="element-341" />
          </button>
        </motion.div>
      </section>

      {/* SECTION 2: OUR BUYER NETWORK */}
      <section className="py-24 md:py-32 bg-gray-50" data-id="element-342">
        <div className="max-w-7xl mx-auto px-6 lg:px-10" data-id="element-343">
          <motion.div {...fadeInUp} className="text-center mb-16" data-id="element-344">
            <p className="text-deal-green text-[11px] tracking-[0.25em] uppercase font-semibold" data-id="element-345">
              OUR BUYER NETWORK
            </p>
          </motion.div>

          {/* Stats Card */}
          <motion.div {...fadeInUp} className="bg-white border border-gray-200 rounded-2xl overflow-hidden mb-16 shadow-sm" data-id="element-346">
            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200" data-id="element-347">
              {/* Stat 1 */}
              <div className="p-10 md:p-12 text-center" data-id="element-348">
                <div className="text-deal-green text-5xl md:text-6xl font-bold mb-4" data-id="element-349">
                  2,000+
                </div>
                <p className="text-gray-400 text-xs uppercase tracking-widest leading-relaxed" data-id="element-350">
                  FAMILY OFFICE & BUYER RELATIONSHIPS
                </p>
              </div>

              {/* Stat 2 */}
              <div className="p-10 md:p-12 text-center" data-id="element-351">
                <div className="text-deal-green text-5xl md:text-6xl font-bold mb-4" data-id="element-352">
                  $600M+
                </div>
                <p className="text-gray-400 text-xs uppercase tracking-widest leading-relaxed" data-id="element-353">
                  CAPITAL DEPLOYED ACROSS 90+ TRANSACTIONS
                </p>
              </div>

              {/* Stat 3 */}
              <div className="p-10 md:p-12 text-center" data-id="element-354">
                <div className="text-deal-green text-5xl md:text-6xl font-bold mb-4" data-id="element-355">
                  5+
                </div>
                <p className="text-gray-400 text-xs uppercase tracking-widest leading-relaxed" data-id="element-356">
                  INDUSTRIES: SERVICES, HEALTHCARE, MANUFACTURING, DISTRIBUTION
                  & MORE
                </p>
              </div>
            </div>
          </motion.div>

          {/* Italic Paragraphs */}
          <motion.div {...fadeInUp} className="max-w-4xl mx-auto text-center space-y-8" data-id="element-357">
            <p className="text-gray-500 text-lg md:text-xl italic leading-relaxed font-light" data-id="element-358">
              Most advisors bring you a list of private equity firms who will
              restructure your company, load it with debt, and flip it in three
              years.
            </p>
            <p className="text-gray-500 text-lg md:text-xl italic leading-relaxed font-light" data-id="element-359">
              Our network is built around a different kind of buyer — family
              offices, independent sponsors, and operators with long-term
              capital who are genuinely interested in preserving your legacy,
              protecting your team, and growing what you've spent years
              building.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 3: OUR PROCESS */}
      <section className="py-24 md:py-32 bg-white" data-id="element-360">
        <div className="max-w-7xl mx-auto px-6 lg:px-10" data-id="element-361">
          <motion.div {...fadeInUp} className="text-center mb-16" data-id="element-362">
            <p className="text-deal-green text-[11px] tracking-[0.25em] uppercase font-semibold" data-id="element-363">
              OUR PROCESS
            </p>
          </motion.div>

          {/* Process Grid */}
          <motion.div {...fadeInUp} className="border border-gray-200 rounded-2xl overflow-hidden bg-gray-100 grid grid-cols-1 md:grid-cols-2 gap-px shadow-sm" data-id="element-364">
            {/* Card 1 */}
            <div className="bg-white p-10 md:p-12 relative overflow-hidden" data-id="element-365">
              <div className="absolute right-0 top-0 text-[120px] md:text-[160px] font-bold text-black/[0.03] leading-none pointer-events-none select-none -translate-y-6 translate-x-6" data-id="element-366">
                01
              </div>
              <div className="relative z-10" data-id="element-367">
                <span className="inline-block border border-deal-green/40 text-deal-green text-xs px-2.5 py-1 rounded-md mb-6 font-mono" data-id="element-368">
                  01
                </span>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4" data-id="element-369">
                  Valuation & Guaranteed Price
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed" data-id="element-370">
                  Our in-house investment banker conducts a full financial
                  analysis of your business and commits to a guaranteed minimum
                  sales price in writing — before a single buyer is contacted.
                  You know your floor before the process begins.
                </p>
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-white p-10 md:p-12 relative overflow-hidden" data-id="element-371">
              <div className="absolute right-0 top-0 text-[120px] md:text-[160px] font-bold text-black/[0.03] leading-none pointer-events-none select-none -translate-y-6 translate-x-6" data-id="element-372">
                02
              </div>
              <div className="relative z-10" data-id="element-373">
                <span className="inline-block border border-deal-green/40 text-deal-green text-xs px-2.5 py-1 rounded-md mb-6 font-mono" data-id="element-374">
                  02
                </span>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4" data-id="element-375">
                  Institutional Marketing Materials
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed" data-id="element-376">
                  We build a full Confidential Information Memorandum, detailed
                  financial model, and professional buyer presentation — the
                  same materials used to sell companies ten times your size.
                  When serious buyers see your business, they see a premium
                  opportunity, not a listing.
                </p>
              </div>
            </div>

            {/* Card 3 */}
            <div className="bg-white p-10 md:p-12 relative overflow-hidden" data-id="element-377">
              <div className="absolute right-0 top-0 text-[120px] md:text-[160px] font-bold text-black/[0.03] leading-none pointer-events-none select-none -translate-y-6 translate-x-6" data-id="element-378">
                03
              </div>
              <div className="relative z-10" data-id="element-379">
                <span className="inline-block border border-deal-green/40 text-deal-green text-xs px-2.5 py-1 rounded-md mb-6 font-mono" data-id="element-380">
                  03
                </span>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4" data-id="element-381">
                  Competitive Auction
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed" data-id="element-382">
                  We run a structured, confidential auction across 2,000+ family
                  offices, PE funds, and strategic buyers — with a hard deadline
                  that forces every qualified buyer to put their best offer
                  forward simultaneously. Competition is the only thing that
                  guarantees maximum value.
                </p>
              </div>
            </div>

            {/* Card 4 */}
            <div className="bg-white p-10 md:p-12 relative overflow-hidden" data-id="element-383">
              <div className="absolute right-0 top-0 text-[120px] md:text-[160px] font-bold text-black/[0.03] leading-none pointer-events-none select-none -translate-y-6 translate-x-6" data-id="element-384">
                04
              </div>
              <div className="relative z-10" data-id="element-385">
                <span className="inline-block border border-deal-green/40 text-deal-green text-xs px-2.5 py-1 rounded-md mb-6 font-mono" data-id="element-386">
                  04
                </span>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4" data-id="element-387">
                  Protected Close
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed" data-id="element-388">
                  We manage every stage of due diligence, negotiation, and
                  closing — protecting you from last-minute re-trades, predatory
                  indemnification clauses, and the tactics buyers use to chip
                  away at your price after you've already said yes.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 4: FINAL CTA */}
      <section className="py-24 md:py-32 bg-gray-50 text-center px-6" data-id="element-389">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="max-w-4xl mx-auto flex flex-col items-center" data-id="element-390">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-391">
            Ready To Find The Dream Buyer For Your Business?
          </h2>
          <p className="text-gray-500 text-lg md:text-xl max-w-2xl mb-12 leading-relaxed font-light" data-id="element-392">
            Apply to schedule a confidential, no-obligation conversation with
            our team. We'll show you exactly how we'd approach your exit, and
            the options of a guaranteed cash buyer in 90 days or less.
          </p>

          <button onClick={onOwnerContactOpen} className="bg-deal-green text-white font-bold text-base px-10 py-5 rounded-lg hover:bg-deal-green-dark transition-colors duration-300 flex items-center gap-2 mb-8" data-id="element-393">
            Book Your Confidential Consultation
            <ArrowRightIcon className="w-5 h-5" data-id="element-394" />
          </button>

          <p className="text-gray-400 text-sm mb-16" data-id="element-395">
            We work with a limited number of clients per quarter. Applications
            are reviewed within 48 hours.
          </p>

          <img src="https://cdn.magicpatterns.com/uploads/vdgv36ytuJeDtCVoSnd1Nw/DF_logo_.png" alt="Deal Flow" className="h-12" style={{
          filter: 'invert(1) hue-rotate(180deg)'
        }} data-id="element-396" />
        </motion.div>
      </section>
    </main>;
}